<template>
  <div class="about-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="hero-container">
        <div class="hero-content">
          <h1>{{ $t('aboutFst.heroTitle') }}</h1>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="main-content">
      <!-- Company Introduction -->
      <section class="company-intro-section">
        <div class="container">
          <div class="section-header reveal">
            <h2 class="section-title">
              <el-icon>
                <OfficeBuilding />
              </el-icon>
              {{ $t('aboutFst.companyIntro.title') }}
            </h2>
            <p class="section-subtitle">{{ $t('aboutFst.companyIntro.subtitle') }}</p>
          </div>

          <!-- 手风琴区域 -->
          <div class="intro-horizontal reveal" style="--delay: 0.15s">
            <!-- 第一列：视频 -->
            <div class="intro-video-col">
              <div class="video-wrapper">
                <video ref="videoPlayer" autoplay loop :muted="isMuted" playsinline class="intro-video"
                  @click="togglePlay">
                  <source :src="require('@/assets/video/fstvideo2.mp4')" type="video/mp4">
                </video>
                <div class="center-play-btn" v-show="!isPlaying" @click.stop="togglePlay">
                  <i class="el-icon-video-play" />
                </div>
                <div class="mute-btn" @click.stop="toggleMute" style="cursor: pointer;">
                  <svg v-if="!isMuted" class="icon" viewBox="0 0 1024 1024" width="18" height="18">
                    <path d="M160 384c-17.7 0-32 14.3-32 32v192c0 17.7 14.3 32 32 32h160l192 192V192L320 384H160z m480-144c-8.8 0-16 7.2-16 16v512c0 8.8 7.2 16 16 16s16-7.2 16-16V256c0-8.8-7.2-16-16-16z m128 96c-8.8 0-16 7.2-16 16v320c0 8.8 7.2 16 16 16s16-7.2 16-16V352c0-8.8-7.2-16-16-16z m128 96c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16s16-7.2 16-16V448c0-8.8-7.2-16-16-16z" fill="currentColor" />
                  </svg>
                  <svg v-else class="icon" viewBox="0 0 1024 1024" width="18" height="18">
                    <path d="M160 384c-17.7 0-32 14.3-32 32v192c0 17.7 14.3 32 32 32h160l192 192V192L320 384H160zM704 512l96-96 48 48-96 96 96 96-48 48-96-96-96 96-48-48 96-96-96-96 48-48 96 96z" fill="currentColor" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- 第二列：公司资质（手风琴卡片 01） -->
            <div class="intro-card-col" :class="{ active: activeCardIndex === 0 }" @mouseenter="expandCard(0)">
              <div class="card-number-badge">01</div>
              <div class="card-vertical-label">
                <span class="vertical-service-item">{{ $t('aboutFst.companyIntro.qualifications.title') }}</span>
              </div>
              <div class="card-expanded-content" :class="{ 'is-open': activeCardIndex === 0 }">
                <div class="card-two-col">
                  <div class="col-title">
                    <h3>{{ $t('aboutFst.companyIntro.qualifications.title') }}</h3>
                  </div>
                  <div class="col-body">
                    <p class="card-desc">
                      {{ $t('aboutFst.companyIntro.qualifications.part1') }}
                      <span class="highlight">{{ $t('aboutFst.companyIntro.qualifications.highlight') }}</span>
                      {{ $t('aboutFst.companyIntro.qualifications.part2') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 第三列：核心业务（手风琴卡片 02） -->
            <div class="intro-service-col" :class="{ active: activeCardIndex === 1 }" @mouseenter="expandCard(1)">
              <div class="card-number-badge">02</div>
              <div class="card-vertical-label">
                <span class="vertical-service-item">{{ $t('aboutFst.companyIntro.coreBusiness.title') }}</span>
              </div>
              <div class="card-expanded-content" :class="{ 'is-open': activeCardIndex === 1 }">
                <div class="card-two-col">
                  <div class="col-title">
                    <h3>{{ $t('aboutFst.companyIntro.coreBusiness.title') }}</h3>
                  </div>
                  <div class="col-body">
                    <div class="service-tags">
                      <div v-for="(item, index) in allServices" :key="index" class="service-tag">
                        {{ item }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 第四列：服务优势（手风琴卡片 03） -->
            <div class="intro-service-col" :class="{ active: activeCardIndex === 2 }" @mouseenter="expandCard(2)">
              <div class="card-number-badge">03</div>
              <div class="card-vertical-label">
                <span class="vertical-service-item">{{ $t('aboutFst.companyIntro.advantages.title') }}</span>
              </div>
              <div class="card-expanded-content" :class="{ 'is-open': activeCardIndex === 2 }">
                <div class="card-two-col">
                  <div class="col-title">
                    <h3>{{ $t('aboutFst.companyIntro.advantages.title') }}</h3>
                  </div>
                  <div class="col-body">
                    <div class="advantage-list">
                      <div class="advantage-list-item" v-for="(item, idx) in $t('aboutFst.companyIntro.advantages.items')" :key="idx">
                        <div class="advantage-list-title">{{ item.title }}</div>
                        <div class="advantage-list-desc">{{ item.content }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mission-statement">
            <el-icon>
              <Star />
            </el-icon>
            <div class="mission-content">
              <h3>{{ $t('aboutFst.companyIntro.mission.title') }}</h3>
              <p class="mission-paragraph">
                {{ $t('aboutFst.companyIntro.mission.prefix') }}
                <span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords1[0]') }}</span>
                {{ $t('aboutFst.companyIntro.mission.keywords2') }}
                <span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[0]') }}</span>
                {{ $t('aboutFst.companyIntro.mission.middle1') }}
                <span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[1]') }}</span>
                {{ $t('aboutFst.companyIntro.mission.middle2') }}
                <span class="highlight">{{ $t('aboutFst.companyIntro.mission.keywords[2]') }}</span>
                {{ $t('aboutFst.companyIntro.mission.suffix') }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Development Timeline -->
      <section class="development-section">
        <div class="section-header center reveal">
          <h2 class="section-title">{{ $t('aboutFst.timeline.title') }}</h2>
          <p class="section-subtitle">{{ $t('aboutFst.timeline.subtitle') }}</p>
        </div>

        <div class="timeline-wrapper">
          <div class="timeline-area">
            <!-- SVG 曲线 -->
            <svg class="timeline-svg" viewBox="0 0 1400 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="curveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="#0077B6" stop-opacity=".6" />
                  <stop offset="50%" stop-color="#4361EE" stop-opacity=".8" />
                  <stop offset="100%" stop-color="#3A0CA3" stop-opacity=".6" />
                </linearGradient>
              </defs>
              <path class="curve-path"
                d="M 70,255 C 93.3,250.5 146.7,237 210,228 C 280,218 310,181.3 350,172 C 390,162.7 450,158.7 490,162 C 536.7,166 590,183.3 630,198 C 676.7,216 730,246.7 770,250 C 816.7,254.3 870,269.3 910,272 C 956.7,275.3 1010,242.7 1050,228 C 1096.7,211.3 1150,178 1190,168 C 1230,158 1283.3,144.7 1330,140" />
              <path class="curve-accent"
                d="M 70,255 C 93.3,250.5 146.7,237 210,228 C 280,218 310,181.3 350,172 C 390,162.7 450,158.7 490,162 C 536.7,166 590,183.3 630,198 C 676.7,216 730,246.7 770,250 C 816.7,254.3 870,269.3 910,272 C 956.7,275.3 1010,242.7 1050,228 C 1096.7,211.3 1150,178 1190,168 C 1230,158 1283.3,144.7 1330,140" />
            </svg>

            <!-- 时间轴事件节点 -->
            <div class="tl-event"
              v-for="(evt, idx) in timelineEvents"
              :key="idx"
              :class="[idx % 2 === 0 ? 'above' : 'below']"
              :style="getEventStyle(idx)">
              <div class="tl-dot" :style="{ background: timelineColors[idx] }"></div>
              <div class="tl-year" :style="{ color: timelineColors[idx] }">
                <span class="yr">{{ evt.year }}</span>
              </div>
              <div class="tl-line"></div>
              <div class="tl-card">
                <div class="tl-index" :style="{ background: timelineColors[idx] }">
                  {{ evt.year }}
                </div>
                <div class="tl-card-text" v-html="evt.content"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Branches Section -->
      <section class="section branches-section">
        <div class="container">
          <div class="section-header center reveal">
            <h2 class="section-title">
              <span class="title-decorator"></span>
              {{ $t('aboutFst.branches.title') }}
            </h2>
            <p class="section-subtitle">{{ $t('aboutFst.branches.subtitle') }}</p>
          </div>

          <!-- 全宽沉浸式展示 -->
          <div class="branch-showcase">
            <!-- 左右切换箭头 -->
            <button class="branch-nav branch-nav-prev" @click="prevBranch">
              <i class="el-icon-arrow-left"></i>
            </button>
            <button class="branch-nav branch-nav-next" @click="nextBranch">
              <i class="el-icon-arrow-right"></i>
            </button>

            <transition :name="branchTransition">
              <div class="branch-card-full" :key="activeBranch">
                <!-- 背景图 -->
                <div class="branch-bg">
                  <div class="branch-bg-image" :key="activeThumb" :style="{ backgroundImage: `url(${currentBranch.images[activeThumb]})` }"></div>
                  <div class="branch-bg-overlay"></div>
                </div>
                <!-- 信息层 -->
                <div class="branch-content-layer">
                  <div class="branch-text-area">
                    <h3 class="branch-city-name">{{ currentBranch.city }}</h3>
                    <p class="branch-description">{{ currentBranch.desc }}</p>
                    <div class="branch-metrics">
                      <div class="metric-item">
                        <div class="metric-icon"><i class="el-icon-office-building"></i></div>
                        <div class="metric-info">
                          <span class="metric-label">{{ $t('aboutFst.common.officeArea') }}</span>
                          <span class="metric-value">{{ currentBranch.officeArea }}</span>
                        </div>
                      </div>
                      <div class="metric-item">
                        <div class="metric-icon"><i class="el-icon-house"></i></div>
                        <div class="metric-info">
                          <span class="metric-label">{{ $t('aboutFst.common.warehouseArea') }}</span>
                          <span class="metric-value">{{ currentBranch.warehouseArea }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- 缩略图廊 -->
                  <div class="branch-thumbnails">
                    <div class="thumb-item" v-for="(img, i) in currentBranch.images" :key="i"
                      :class="{ active: i === activeThumb }"
                      @click="activeThumb = i">
                      <el-image :src="img" fit="cover" :preview-src-list="currentBranch.images" />
                    </div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- 底部圆点指示器 -->
            <div class="branch-dots">
              <span v-for="(branch, index) in branches" :key="index"
                class="dot" :class="{ active: activeBranch === branch.key }"
                @click="activeBranch = branch.key">
                <span class="dot-label">{{ branch.cityWithYear }}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Memberships Section -->
      <section class="section memberships-section">
        <div class="container">
          <div class="section-header center reveal">
            <h2 class="section-title">{{ $t('aboutFst.memberships.title') }}</h2>
            <p class="section-subtitle">{{ $t('aboutFst.memberships.subtitle') }}</p>
          </div>

          <div class="membership-marquee">
            <div class="marquee-track">
              <div class="marquee-content">
                <div class="membership-card-new" v-for="(item, index) in memberships" :key="'a'+index">
                  <div class="card-face card-front">
                    <el-image :src="item.img" fit="contain" class="membership-logo" />
                  </div>
                  <div class="card-face card-back">
                    <span class="membership-name-text">{{ item.name }}</span>
                  </div>
                </div>
                <!-- 复制一份实现无缝循环 -->
                <div class="membership-card-new" v-for="(item, index) in memberships" :key="'b'+index">
                  <div class="card-face card-front">
                    <el-image :src="item.img" fit="contain" class="membership-logo" />
                  </div>
                  <div class="card-face card-back">
                    <span class="membership-name-text">{{ item.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Certificates Section -->
      <section class="section certificates-section">
        <div class="container">
          <div class="section-header center reveal">
            <h2 class="section-title">{{ $t('aboutFst.certificates.title') }}</h2>
            <p class="section-subtitle">{{ $t('aboutFst.certificates.subtitle') }}</p>
          </div>

          <div class="cert-gallery">
            <div class="cert-card" v-for="(cert, index) in certificates" :key="index"
              :class="'cert-pos-' + index"
              :style="{ '--delay': index * 0.1 + 's' }">
              <div class="cert-card-inner">
                <el-image :src="cert.img" fit="contain" class="cert-img"
                  :preview-src-list="certificates.map(c => c.img)" />
                <div class="cert-label">{{ cert.name }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script>
export default {
  name: 'About',
  data() {
    return {
      isMuted: true,
      isPlaying: true,
      isMobileView: false,
      activeBranch: 'beijing',
      activeThumb: 0,
      activeCardIndex: 0,
      branchTransition: 'slide-left',
      branchAutoTimer: null,
      thumbAutoTimer: null,

      containerWidth: 1200,
      isMobile: false,
      isTablet: false,
      timelineColors: [
        '#0077B6', '#0096C7', '#00B4D8', '#0088CC', '#4895EF',
        '#4361EE', '#4CC9F0', '#5E60CE', '#7209B7', '#3A0CA3'
      ],
    }
  },
  computed: {
    allServices() {
      return this.$t('aboutFst.companyIntro.coreBusiness.services');
    },
    advantageTitles() {
      return this.$t('aboutFst.companyIntro.advantages.items').map(item => item.title);
    },
    branches() {
      return [{
        key: 'beijing',
        city: this.$t('aboutFst.branches.beijing.city'),
        cityWithYear: this.$t('aboutFst.branches.beijing.cityWithYear'),
        year: this.$t('aboutFst.branches.beijing.year'),
        desc: this.$t('aboutFst.branches.beijing.desc'),
        officeArea: this.$t('aboutFst.branches.beijing.officeArea'),
        warehouseArea: this.$t('aboutFst.branches.beijing.warehouseArea'),
        images: [
          require('@/assets/images/北京办公室.jpg'),
          require('@/assets/images/北京办公室2.jpg'),
          require('@/assets/images/北京冷库1.jpg'),
          require('@/assets/images/北京冷库2.jpg'),
          require('@/assets/images/slk.jpg'),
        ]
      },
      {
        key: 'shanghai',
        city: this.$t('aboutFst.branches.shanghai.city'),
        cityWithYear: this.$t('aboutFst.branches.shanghai.cityWithYear'),
        year: this.$t('aboutFst.branches.shanghai.year'),
        desc: this.$t('aboutFst.branches.shanghai.desc'),
        officeArea: this.$t('aboutFst.branches.shanghai.officeArea'),
        warehouseArea: this.$t('aboutFst.branches.shanghai.warehouseArea'),
        images: [
          require('@/assets/images/shky.png'),
          require('@/assets/images/shhy.jpg'),
          require('@/assets/images/上海变频冷库.jpg'),
          require('@/assets/images/shlk.jpg'),
        ]
      },
      {
        key: 'guangzhou',
        city: this.$t('aboutFst.branches.guangzhou.city'),
        cityWithYear: this.$t('aboutFst.branches.guangzhou.cityWithYear'),
        year: this.$t('aboutFst.branches.guangzhou.year'),
        desc: this.$t('aboutFst.branches.guangzhou.desc'),
        officeArea: this.$t('aboutFst.branches.guangzhou.officeArea'),
        warehouseArea: this.$t('aboutFst.branches.guangzhou.warehouseArea'),
        images: [
          require('@/assets/images/gzbgs.jpg'),
          require('@/assets/images/gz3.jpg'),
          require('@/assets/images/广州仓库2.jpg'),
          require('@/assets/images/广州冷库2.jpg'),
        ]
      },
      {
        key: 'zhengzhou',
        city: this.$t('aboutFst.branches.zhengzhou.city'),
        cityWithYear: this.$t('aboutFst.branches.zhengzhou.cityWithYear'),
        year: this.$t('aboutFst.branches.zhengzhou.year'),
        desc: this.$t('aboutFst.branches.zhengzhou.desc'),
        officeArea: this.$t('aboutFst.branches.zhengzhou.officeArea'),
        warehouseArea: this.$t('aboutFst.branches.zhengzhou.warehouseArea'),
        images: [
          require('@/assets/images/郑州办公室.jpg'),
        ]
      },
      {
        key: 'chengdu',
        city: this.$t('aboutFst.branches.chengdu.city'),
        cityWithYear: this.$t('aboutFst.branches.chengdu.cityWithYear'),
        year: this.$t('aboutFst.branches.chengdu.year'),
        desc: this.$t('aboutFst.branches.chengdu.desc'),
        officeArea: this.$t('aboutFst.branches.chengdu.officeArea'),
        warehouseArea: this.$t('aboutFst.branches.chengdu.warehouseArea'),
        images: [
          require('@/assets/images/成都办公室1.png'),
          require('@/assets/images/cdkf1.jpg'),
          require('@/assets/images/cdkf2.jpg'),
        ]
      }
      ]
    },
    certificates() {
      return [
        {
          img: require('@/assets/images/航协会员证书_01.png'),
          name: this.$t('aboutFst.certificates.cert1')
        },
        {
          img: require('@/assets/images/质量管理体系_01.png'),
          name: this.$t('aboutFst.certificates.cert2')
        },
        {
          img: require('@/assets/images/危品WCA_01.png'),
          name: this.$t('aboutFst.certificates.cert3')
        },
        {
          img: require('@/assets/images/医药WCA_01.png'),
          name: this.$t('aboutFst.certificates.cert4')
        },
        {
          img: require('@/assets/images/冷库温度显示装置检定报告_01.png'),
          name: this.$t('aboutFst.certificates.cert5')
        },
        {
          img: require('@/assets/images/CAST证书_01.png'),
          name: this.$t('aboutFst.certificates.cert6')
        },
        {
          img: require('@/assets/images/IATA北京2026_01.png'),
          name: this.$t('aboutFst.certificates.cert7')
        },
        {
          img: require('@/assets/images/GSP.png'),
          name: this.$t('aboutFst.certificates.cert8')
        }
      ]
    },
    memberships() {
      return [
        {
          img: require('@/assets/images/iatalogo.png'),
          name: this.$t('aboutFst.memberships.iata')
        },
        {
          img: require('@/assets/images/logo_wcaworld2.png'),
          name: this.$t('aboutFst.memberships.wca')
        },
        {
          img: require('@/assets/images/logo_wcaworld1.png'),
          name: this.$t('aboutFst.memberships.wcaDangerous')
        },
        {
          img: require('@/assets/images/jc1.png'),
          name: this.$t('aboutFst.memberships.jctrans')
        },
        {
          img: require('@/assets/images/jc2.jpg'),
          name: this.$t('aboutFst.memberships.jctransCert')
        }
      ]
    },
    currentBranch() {
      return this.branches.find(b => b.key === this.activeBranch) || this.branches[0];
    },
    timelineEvents() {
      const events = this.$t('aboutFst.timeline.events');
      return [...events].reverse();
    },
    carouselHeight() {
      return this.isMobile ? '280px' : '350px';
    },
    certCarouselHeight() {
      return this.isMobile ? '280px' : (this.isTablet ? '320px' : '350px');
    }
  },
  mounted() {
    this.checkMobileView();
    window.addEventListener('resize', this.checkMobileView);
    console.log(this.$refs, "视频播放")
    this.checkViewport();
    window.addEventListener('resize', this.checkViewport);

    const savedBranch = localStorage.getItem('activeBranch');
    if (savedBranch && this.branches.some(b => b.key === savedBranch)) {
      this.activeBranch = savedBranch;
    }
    this.updateContainerWidth();
    window.addEventListener('resize', this.updateContainerWidth);
    this.tryAutoPlayWithSound();
    this.$nextTick(() => {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.play()
          .then(() => this.isPlaying = true)
          .catch(e => {
            console.log("Autoplay blocked:", e);
            this.isPlaying = false;
          });
      }
    });
    this.$nextTick(() => {
      this.initTimelineAnimation();
      this.initRevealObserver();
    });
    this.startThumbAutoPlay();
    this.startBranchAutoPlay();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkViewport);
    window.removeEventListener('resize', this.updateContainerWidth);
    window.removeEventListener('resize', this.checkMobileView);
    if (this._timelineObserver) {
      this._timelineObserver.disconnect();
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
    }
    this.stopThumbAutoPlay();
    this.stopBranchAutoPlay();
  },
  watch: {
    activeBranch(newVal) {
      localStorage.setItem('activeBranch', newVal);
      this.activeThumb = 0;
      this.stopThumbAutoPlay();
      this.startThumbAutoPlay();
    }
  },
  methods: {
    expandCard(index) {
      this.activeCardIndex = index;
    },
    checkMobileView() {
      this.isMobileView = window.innerWidth <= 768;
    },
    togglePlay() {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      if (this.isPlaying) {
        video.pause();
      } else {
        video.play().catch(e => {
          console.log("Playback failed:", e);
          video.muted = true;
          video.play();
        });
      }
      this.isPlaying = !this.isPlaying;
    },

    toggleMute() {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      this.isMuted = !this.isMuted;
      video.muted = this.isMuted;
    },
    async tryAutoPlayWithSound() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      try {
        await video.play();
        video.muted = true;
        this.isMuted = true;
        this.isPlaying = true;
      } catch (err) {
        console.log("自动播放被阻止，需用户交互");
        video.muted = true;
        this.isMuted = true;
        this.isPlaying = false;
      }
    },
    toggleVideoSound() {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      video.muted = !video.muted;
      this.isMuted = video.muted;
    },
    checkViewport() {
      const width = window.innerWidth;
      this.isMobile = width <= 768;
      this.isTablet = width > 768 && width <= 1024;
    },
    updateContainerWidth() {
      if (this.$el) {
        this.containerWidth = this.$el.querySelector('.container').offsetWidth;
      }
    },


    getAdvantageIcon(index) {
      const icons = ['Connection', 'User', 'DataBoard', 'House'];
      return icons[index] || 'Star';
    },

    getEventPosition(index) {
      const positions = [
        { left: '2.5%', top: '96.25%' },
        { left: '15.3%', top: '91.1%' },
        { left: '28.7%', top: '73.3%' },
        { left: '41.7%', top: '55.7%' },
        { left: '52.2%', top: '46.2%' },
        { left: '59.5%', top: '35.25%' },
        { left: '68.2%', top: '20%' },
        { left: '77%', top: '13.5%' },
        { left: '85.5%', top: '8.5%' },
        { left: '93%', top: '5.2%' }
      ];
      return positions[index] || { left: '50%', top: '50%' };
    },

    getCardOffset(index) {
      return {};
    },
    initTimelineAnimation() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px'
      });

      const items = this.$el.querySelectorAll('.tl-event');
      items.forEach((item, i) => {
        item.style.transitionDelay = `${i * 0.1}s`;
        observer.observe(item);
      });

      this._timelineObserver = observer;
    },

    initRevealObserver() {
      const els = this.$el.querySelectorAll('.reveal');
      if (!els.length) return;

      this._revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = getComputedStyle(el).getPropertyValue('--delay') || '0s';
            const ms = parseFloat(delay) * 1000;
            setTimeout(() => {
              el.classList.add('revealed');
            }, ms);
            this._revealObserver.unobserve(el);
          }
        });
      }, {
        threshold: 0.08,
        rootMargin: '0px 0px -30px 0px'
      });

      els.forEach(el => {
        this._revealObserver.observe(el);
      });
    },

    prevBranch() {
      const keys = this.branches.map(b => b.key);
      const currentIndex = keys.indexOf(this.activeBranch);
      const prevIndex = (currentIndex - 1 + keys.length) % keys.length;
      this.branchTransition = 'slide-right';
      this.activeBranch = keys[prevIndex];
      this.resetBranchAutoPlay();
    },
    nextBranch() {
      const keys = this.branches.map(b => b.key);
      const currentIndex = keys.indexOf(this.activeBranch);
      const nextIndex = (currentIndex + 1) % keys.length;
      this.branchTransition = 'slide-left';
      this.activeBranch = keys[nextIndex];
      this.resetBranchAutoPlay();
    },
    getCertRotation(index) {
      const rotations = [-3, 2, -1.5, 2.5, -2, 1.5, -2.5, 1];
      return rotations[index % rotations.length];
    },
    getEventStyle(index) {
      const positions = [
        { left: '5%',  top: '56.6%' },
        { left: '15%', top: '53.2%' },
        { left: '25%', top: '46.2%' },
        { left: '35%', top: '45.0%' },
        { left: '45%', top: '49.5%' },
        { left: '55%', top: '56.0%' },
        { left: '65%', top: '58.8%' },
        { left: '75%', top: '53.2%' },
        { left: '85%', top: '45.7%' },
        { left: '95%', top: '42.2%' }
      ];
      const pos = positions[index] || { left: '50%', top: '50%' };
      return { left: pos.left, top: pos.top };
    },
    startThumbAutoPlay() {
      this.stopThumbAutoPlay();
      this.thumbAutoTimer = setInterval(() => {
        const images = this.currentBranch.images;
        const nextIndex = (this.activeThumb + 1) % images.length;
        if (nextIndex === 0) {
          this.nextBranch();
        } else {
          this.activeThumb = nextIndex;
        }
      }, 3000);
    },
    stopThumbAutoPlay() {
      if (this.thumbAutoTimer) {
        clearInterval(this.thumbAutoTimer);
        this.thumbAutoTimer = null;
      }
    },
    startBranchAutoPlay() {
      // 由缩略图触发，无需独立定时器
    },
    stopBranchAutoPlay() {
      // 无需独立定时器
    },
    resetBranchAutoPlay() {
      this.stopThumbAutoPlay();
      this.startThumbAutoPlay();
    }
  }
}
</script>

<style lang="scss" scoped>
// === Design System Variables (aligned with Home.vue) ===
$primary: #0052CC;
$primary-dark: #0043A8;
$primary-light: #3B7FFF;
$silver: #8E9EAB;
$silver-light: #A8B5C0;
$dark: #0A1628;
$light: #F4F7FB;
$body: #5B6C7F;
$heading: #0A1628;

.about-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-x: hidden;
  font-family: 'Rubik', 'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: $body;
  line-height: 1.6;
  background: linear-gradient(rgba(244, 247, 251, 0.92), rgba(244, 247, 251, 0.92)),
    url('../assets/images/背景1.jpg') center/cover no-repeat;

  .hero-banner {
    width: 100%;
    height: 60vh;
    min-height: 400px;
    position: relative;
    background: linear-gradient(135deg, rgba(10, 22, 40, .75) 0%, rgba(0, 82, 204, .3) 100%),
      url('../assets/images/zjwm.png') center/cover no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius:20px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 60, 120, 0.1);

    .hero-container {
      max-width: 1200px;
      width: 90%;
      margin: 0 auto;
      padding: 40px;
      background-color: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(8px);
      border-radius: 12px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: 0 8px 32px rgba(0, 60, 120, 0.1);
    }

    .hero-content {
      max-width: 800px;
      text-align: center;
      animation: fadeInUp 1.2s ease-out;

      h1 {
        font-size: 2.6rem;
        font-weight: 800;
        margin-bottom: 1.5rem;
        color: #fff;
        text-shadow: 0 2px 12px rgba(0, 60, 120, 0.6);
        letter-spacing: -0.5px;
        line-height: 1.2;
        position: relative;
        display: inline-block;

        &::after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: rgba(255, 255, 255, 0.7);
          margin: 20px auto;
          border-radius: 2px;
        }
      }

      .subtitle {
        font-size: clamp(1.2rem, 3vw, 1.5rem);
        font-weight: 500;
        color: #fff;
        margin-bottom: 2rem;
        letter-spacing: 1.5px;
        line-height: 1.4;
        position: relative;
        display: inline-block;
        padding: clamp(0.8rem, 2vw, 1.2rem) clamp(1.5rem, 4vw, 2.5rem);
        background: linear-gradient(to right, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: 50px;
        backdrop-filter: blur(6px);
        box-shadow: 0 4px 15px rgba(0, 60, 120, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.2);

        &::after {
          content: '';
          display: block;
          width: 40%;
          height: 2px;
          background: linear-gradient(to right, rgba(255, 255, 255, 0.8), transparent);
          margin: 14px auto 0;
          opacity: 0.7;
        }
      }
    }
  }

  .main-content {
    flex: 1;
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;

    .section {
      margin-bottom: 40px;

      &:last-child {
        margin-bottom: 0;
      }

      .container {
        width: 100%;
      }
    }

    .section-header {
      margin-bottom: 50px;

      &.center {
        text-align: center;
      }

      .section-title {
        font-size: 2.6rem;
        font-weight: 700;
        margin-bottom: 0;
        color: $heading;
        position: relative;
        display: inline-block;
        letter-spacing: -0.5px;

        &::after {
          content: '';
          display: block;
          background: linear-gradient(to right, $primary, $primary-light);
          height: 4px;
          width: 80px;
          border-radius: 2px;
          margin: 12px auto 0;
        }
      }

      .section-subtitle {
        font-size: 1rem;
        color: $body;
        font-weight: 400;
        margin-top: 15px;
        letter-spacing: 0;
      }
    }

    /* 公司简介横向卡片布局 */
    .company-intro-section {
      padding: 90px 0;
      padding-bottom: 0px;
      position: relative;
    }

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .section-header {
      text-align: center;
      margin-bottom: 50px;
    }

    .section-title {
      font-size: 2.6rem;
      color: $heading;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;
      font-weight: 700;
      letter-spacing: -0.5px;
    }

    .section-title .el-icon {
      font-size: 1.8rem;
      margin-right: 15px;
      color: $primary;
    }

    .section-subtitle {
      font-size: 1rem;
      color: $body;
      margin-top: 15px;
    }

    .title-decoration {
      width: 60px;
      height: 3px;
      background: linear-gradient(to right, $primary, $primary-light);
      margin: 0 auto;
      border-radius: 3px;
    }

    /* 横向卡片容器 */
    .intro-horizontal {
      display: flex;
      gap: 12px;
      margin-bottom: 50px;
      align-items: stretch;
      min-height: 380px; /* 固定最小高度，防止跳动 */
    }

    /* 视频列 */
    .intro-video-col {
      flex: 0 0 38%;
      min-width: 0;
    }

    .video-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      overflow: hidden;
      background: #000;
    }

    .intro-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .center-play-btn {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: rgba(0, 82, 204, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: all 0.3s;
    }

    .center-play-btn i {
      font-size: 26px;
      margin-left: 4px;
    }

    .center-play-btn:hover {
      transform: translate(-50%, -50%) scale(1.1);
      background: rgba(0, 82, 204, 0.9);
    }

    .mute-btn {
      position: absolute;
      bottom: 12px;
      right: 12px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: background 0.3s;
    }

    .mute-btn:hover {
      background: rgba(0, 0, 0, 0.75);
    }

    /* 手风琴卡片（收起时宽度 60px） */
    .intro-card-col,
    .intro-service-col {
      flex: 0 0 60px;
      min-width: 0;
      background: #fff;
      border-radius: 20px;
      padding: 20px 10px;
      box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(0, 82, 204, 0.06);
      position: relative;
      cursor: pointer;
      transition: flex 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                  padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;

      &:hover {
        box-shadow: 0 8px 32px rgba(0, 82, 204, 0.12);
        transform: translateY(-2px);
      }
    }

    /* 展开状态（flex 设为 1，宽度自动撑满） */
    .intro-card-col.active,
    .intro-service-col.active {
      flex: 1;
      padding: 32px 28px;
      align-items: flex-start;
    }

    /* 序号徽章 —— 始终固定于卡片顶部 */
    .card-number-badge {
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #e8eaed;
      color: #1a1a2e;
      font-size: 15px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
      margin: 0;
      flex-shrink: 0;
      z-index: 2;
    }

    /* 展开时徽章移到右上角 */
    .intro-card-col.active .card-number-badge,
    .intro-service-col.active .card-number-badge {
      top: 20px;
      right: 20px;
      left: auto;
      transform: none;
    }

    /* 竖向文字标签（收起状态，垂直居中） */
    .card-vertical-label {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 100%;
      max-height: 200px;
      overflow: hidden;
      transition: opacity 0.35s ease, max-height 0.35s ease;
      flex: 1;
      margin-top: 50px; /* 避开绝对定位的 card-number-badge（top:10px + height:44px） */
    }

    .intro-card-col.active .card-vertical-label,
    .intro-service-col.active .card-vertical-label {
      opacity: 0;
      max-height: 0;
      pointer-events: none;
    }

    .vertical-service-item {
      writing-mode: vertical-rl;
      text-orientation: mixed;
      font-size: 17px;
      font-weight: 700;
      color: $primary;
      letter-spacing: 3px;
      transition: all 0.3s;
    }

    /* ---------- 展开内容（使用 max-height 过渡，取代 v-show） ---------- */
    .card-expanded-content {
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      width: 100%;
      transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.4s ease 0.1s;
    }

    .card-expanded-content.is-open {
      max-height: 600px;        /* 设为一个足够容纳所有内容的值 */
      opacity: 1;
    }

    /* 展开后的两列布局（标题 + 内容） */
    .card-two-col {
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
      width: 100%;
    }

    .col-title {
      flex: 0 0 auto;
      min-width: auto;
    }
    .col-title h3 {
      font-size: 1.4rem;
      color: $heading;
      margin: 0;
      font-weight: 800;
      white-space: normal;
    }

    .col-body {
      flex: 1;
      min-width: 0;
      width: 100%;
    }

    /* 其余内部样式（描述、标签、优势列表等）保持不变 */
    .card-desc {
      color: $body;
      line-height: 1.75;
      margin: 0;
      font-size: 0.92rem;
    }
    .highlight {
      font-weight: bold;
      color: $primary;
    }
    .service-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .service-tag {
      font-size: 13px;
      color: $heading;
      font-weight: 500;
      padding: 5px 13px;
      background: rgba(0, 82, 204, 0.06);
      border-radius: 20px;
      transition: all 0.3s;
    }
    .service-tag:hover {
      background: $primary;
      color: #fff;
    }
    .advantage-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .advantage-list-item {
      padding: 8px 12px;
      background: rgba(0, 82, 204, 0.04);
      border-radius: 8px;
      border-left: 3px solid $primary;
      transition: all 0.3s;
    }
    .advantage-list-item:hover {
      background: rgba(0, 82, 204, 0.08);
      transform: translateX(4px);
    }
    .advantage-list-title {
      font-size: 0.9rem;
      font-weight: 700;
      color: $heading;
      margin-bottom: 2px;
    }
    .advantage-list-desc {
      font-size: 0.82rem;
      color: $body;
      line-height: 1.45;
    }

    /* 使命宣言 */
    .mission-statement {
      background: linear-gradient(135deg, $light 0%, rgba(0, 82, 204, 0.04) 100%);
      border-radius: 12px;
      padding: 30px;
      display: flex;
      align-items: center;
      gap: 20px;
      border: 1px solid rgba(0, 82, 204, .08);
    }

    .mission-statement .el-icon {
      font-size: 2.5rem;
      color: $primary;
      flex-shrink: 0;
    }

    .mission-content h3 {
      color: $heading;
      margin: 0 0 10px 0;
      font-size: 1.5rem;
    }

    .mission-content p {
      color: $body;
      line-height: 1.8;
      margin: 0;
    }

    /* 响应式设计 */
    @media (max-width: 1100px) {
      .intro-horizontal {
        min-height: 280px;
      }
      .intro-card-col.active,
      .intro-service-col.active {
        flex: 1;
      }
    }

    @media (max-width: 900px) {
      .intro-horizontal {
        flex-wrap: wrap;
        min-height: auto;
      }
      .intro-video-col {
        flex: 0 0 100%;
      }
      .video-wrapper {
        height: 280px;
      }
      .intro-card-col,
      .intro-service-col {
        flex: 0 0 60px;
      }
      .intro-card-col.active,
      .intro-service-col.active {
        flex: 0 0 100%;
        padding: 32px 20px;
      }
      .card-two-col {
        flex-direction: column;
        gap: 12px;
      }
      .col-title h3 {
        white-space: normal;
      }
    }

    @media (max-width: 768px) {
      .mission-statement {
        flex-direction: column;
        text-align: center;
      }
      .section-title {
        font-size: 1.8rem;
      }
      .intro-horizontal {
        flex-direction: column;
        gap: 14px;
      }
      .intro-video-col,
      .intro-card-col,
      .intro-service-col,
      .intro-service-col.active {
        flex: 0 0 100%;
      }
      .video-wrapper {
        height: 220px;
      }
      .intro-service-col {
        min-height: 160px;
      }
      .intro-service-col.active {
        min-height: auto;
      }
    }

    @media (max-width: 480px) {
      .company-intro-section {
        padding: 60px 0;
      }
      .intro-card-col,
      .intro-service-col {
        padding: 22px 18px;
      }
      .service-tags {
        justify-content: center;
      }
    }

    .development-section {
      padding: 60px 0;
      padding-bottom: 0px;
      position: relative;

      &::before,
      &::after {
        content: '';
        position: absolute;
        border-radius: 50%;
        filter: blur(120px);
        opacity: 0.12;
        pointer-events: none;
      }
      &::before {
        width: 700px; height: 700px;
        top: -300px; right: -200px;
      }
      &::after {
        width: 500px; height: 500px;
        bottom: -200px; left: -100px;
      }

      .section-header {
        text-align: center;
        margin-bottom: 50px;
        position: relative;
        z-index: 1;
      }

      .section-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: linear-gradient(135deg, rgba(0, 82, 204, .08), rgba(59, 127, 255, .08));
        color: $primary;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        padding: 6px 20px;
        border-radius: 100px;
        margin-bottom: 18px;
      }

      .section-title {
        font-size: 2.6rem;
        font-weight: 700;
        color: $heading;
        margin-bottom: 0;
        letter-spacing: -0.5px;
        line-height: 1.2;
      }

      .section-subtitle {
        font-size: 1rem;
        color: $body;
        font-weight: 400;
        max-width: 520px;
        margin: 15px auto 0;
      }
    }

    /* Timeline Container */
    .timeline-wrapper {
      max-width: 1320px;
      margin: 0 auto;
      padding: 0 40px;
      position: relative;
      z-index: 1;
    }

    .timeline-area {
      position: relative;
      height: 560px;
      width: 100%;
    }

    /* SVG Curve */
    .timeline-svg {
      position: absolute;
      left: 0;
      top: 140px;
      width: 100%;
      height: 280px;
      z-index: 1;
      overflow: visible;

      .curve-path {
        fill: none;
        stroke: #cbd5e1;
        stroke-width: 5;
        stroke-linecap: round;
        filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.06));
        stroke-dasharray: 3000;
        animation: drawLine 2.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      .curve-accent {
        fill: none;
        stroke: url(#curveGrad);
        stroke-width: 2.5;
        stroke-linecap: round;
        stroke-dasharray: 8 12;
        opacity: 0.6;
      }
    }

    @keyframes drawLine {
      from { stroke-dashoffset: 3000; }
      to { stroke-dashoffset: 0; }
    }

    /* Event Item */
    .tl-event {
      position: absolute;
      width: 0;
      height: 0;
      z-index: 3;
      opacity: 0;
      transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);

      &.visible {
        opacity: 1;
      }

      &.above {
        transform: translateY(24px);
        &.visible { transform: translateY(0); }
      }

      &.below {
        transform: translateY(-24px);
        &.visible { transform: translateY(0); }
      }
    }

    /* Dot */
    .tl-dot {
      position: absolute;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      left: -9px;
      top: -9px;
      z-index: 4;
      box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.9),
                  0 2px 10px rgba(0, 0, 0, 0.12);
      transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
      cursor: pointer;

      &::after {
        content: '';
        position: absolute;
        inset: 4px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.5);
      }

      &::before {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        border: 2px solid currentColor;
        opacity: 0;
        transition: all 0.35s ease;
      }
    }

    .tl-event:hover .tl-dot {
      transform: scale(1.35);
      box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.95),
                  0 4px 20px rgba(0, 0, 0, 0.15);

      &::before {
        opacity: 0.3;
        inset: -10px;
      }
    }

    /* Year Label */
    .tl-year {
      position: absolute;
      left: 0;
      font-size: 28px;
      font-weight: 800;
      letter-spacing: -0.5px;
      white-space: nowrap;
      line-height: 1;
      transition: all 0.3s ease;

      .yr {
        display: block;
      }
    }

    .tl-event.above .tl-year {
      top: 20px;
      transform: translateX(-50%);
    }

    .tl-event.below .tl-year {
      bottom: 20px;
      transform: translateX(-50%);
    }

    .tl-event:hover .tl-year {
      transform: translateX(-50%) scale(1.05);
    }

    /* Connector Line */
    .tl-line {
      position: absolute;
      left: -1px;
      width: 2px;
      border-radius: 1px;
      opacity: 0.45;
    }

    .tl-event.above .tl-line {
      bottom: 18px;
      height: 64px;
      background: linear-gradient(to top, var(--dot-color, #3b82f6), transparent);
    }

    .tl-event.below .tl-line {
      top: 18px;
      height: 64px;
      background: linear-gradient(to bottom, var(--dot-color, #3b82f6), transparent);
    }

    /* Content Card */
    .tl-card {
      position: absolute;
      left: 0;
      width: 175px;
      transform: translateX(-50%);
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.9);
      border-radius: 14px;
      padding: 14px 14px 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05),
                  0 8px 24px rgba(0, 0, 0, 0.04);
      transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      cursor: default;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        border-radius: 14px 14px 0 0;
        background: var(--dot-color, #3b82f6);
        opacity: 0;
        transition: opacity 0.35s ease;
      }
    }

    .tl-event.above .tl-card {
      bottom: 88px;
    }

    .tl-event.below .tl-card {
      top: 88px;
    }

    .tl-event:hover .tl-card {
      transform: translateX(-50%) translateY(-4px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08),
                  0 16px 40px rgba(0, 82, 204, 0.1);
      border-color: rgba(0, 82, 204, 0.12);
      &::before {
        opacity: 1;
      }
    }

    .tl-card-text {
      font-size: 12.5px;
      line-height: 1.65;
      color: $body;
      font-weight: 400;
      word-break: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
    }

    /* Index Badge */
    .tl-index {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 22px;
      border-radius: 50%;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 8px;
      opacity: 0.85;
    }

    /* ===== Tablet ===== */
    @media (max-width: 1100px) {
      .timeline-wrapper {
        padding: 0 24px;
      }

      .tl-card {
        width: 140px;
        padding: 10px 10px 8px;
        border-radius: 12px;
      }

      .tl-card-text {
        font-size: 11.5px;
      }

      .tl-year {
        font-size: 22px;
      }

      .tl-index {
        width: 18px;
        height: 18px;
        font-size: 10px;
      }

      .timeline-area {
        height: 500px;
      }
    }

    /* ===== Mobile — vertical layout ===== */
    @media (max-width: 768px) {
      .development-section {
        padding: 50px 0;

        .section-header {
          margin-bottom: 40px;
          padding: 0 16px;
        }
      }

      .timeline-wrapper {
        padding: 0 20px;
      }

      .timeline-svg {
        display: none;
      }

      .timeline-area {
        height: auto;
        position: relative;
        padding-left: 32px;

        &::before {
          content: '';
          position: absolute;
          left: 15px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(180deg,
              rgba(0, 82, 204, 0) 0%,
              rgba(0, 82, 204, 0.2) 5%,
              rgba(59, 127, 255, 0.35) 50%,
              rgba(0, 82, 204, 0.2) 95%,
              rgba(0, 82, 204, 0) 100%);
          border-radius: 3px;
        }
      }

      .tl-event {
        position: relative !important;
        left: auto !important;
        top: auto !important;
        width: 100% !important;
        height: auto;
        margin-bottom: 32px;
        transform: none !important;
        opacity: 1 !important;
      }

      .tl-dot {
        position: absolute;
        left: -25px;
        top: 4px;
        width: 14px;
        height: 14px;

        &::after {
          inset: 3px;
        }
      }

      .tl-year {
        position: relative;
        left: auto;
        top: auto;
        bottom: auto;
        transform: none !important;
        font-size: 24px;
        margin-bottom: 6px;
      }

      .tl-line {
        display: none;
      }

      .tl-card {
        position: relative;
        left: auto !important;
        top: auto !important;
        bottom: auto !important;
        transform: none !important;
        width: 100%;
        padding: 14px 16px;
      }

      .tl-card-text {
        font-size: 13px;
      }

      .tl-event:hover .tl-card {
        transform: translateY(-3px) !important;
      }
    }

    /* 证书会员 Marquee 样式 */
    .memberships-section {
      padding: 60px 0;
      padding-bottom: 0px;
      position: relative;

      .section-header {
        margin-bottom: 50px;
        position: relative;

        .section-title {
          color: $heading;
          font-size: 2.6rem;
          font-weight: 700;
          letter-spacing: -0.5px;

          &::after {
            background: linear-gradient(to right, $primary, $primary-light);
            height: 4px;
            width: 80px;
            border-radius: 2px;
          }
        }

        .section-subtitle {
          color: $body;
          font-size: 1rem;
          font-weight: 400;
          margin-top: 15px;
        }
      }
    }

    .membership-marquee {
      position: relative;
      overflow: hidden;
      padding: 40px 0;

      &::before, &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 120px;
        z-index: 3;
        pointer-events: none;
      }
      &::before {
        left: 0;
        background: linear-gradient(90deg, $light, transparent);
      }
      &::after {
        right: 0;
        background: linear-gradient(270deg, $light, transparent);
      }

      &:hover .marquee-content {
        animation-play-state: paused;
      }
    }

    .marquee-content {
      display: flex;
      align-items: center;
      gap: 48px;
      animation: marqueeScroll 25s linear infinite;
      width: max-content;
    }

    @keyframes marqueeScroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }

    .membership-card-new {
      width: 200px;
      height: 160px;
      perspective: 800px;
      flex-shrink: 0;
      cursor: pointer;
      position: relative;

      .card-face {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        backface-visibility: hidden;
        transition: transform .6s cubic-bezier(.4,0,.2,1);
        background: #fff;
        box-shadow: 0 4px 20px rgba(0,0,0,0.06);
        border: 1px solid rgba(0,82,204,0.08);
        padding: 24px;
      }

      .card-front {
        transform: rotateY(0deg);
      }
      .card-back {
        transform: rotateY(180deg);
        background: linear-gradient(135deg, $primary, $primary-light);
        color: #fff;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        padding: 20px;
      }

      &:hover {
        .card-front { transform: rotateY(-180deg); }
        .card-back { transform: rotateY(0deg); }
      }

      .membership-logo {
        max-width: 80%;
        max-height: 80%;
        transition: transform .3s ease;
      }
    }

  :root {
    --primary-color: #0052CC;
    --primary-rgb: 0, 82, 204;
    --text-light: #8E9EAB;
    --transition: all 0.3s ease;
  }

  /* 分公司样式 - 全宽沉浸式 */
  .branches-section {
    background: var(--white);
    padding: 60px 0;
    padding-bottom: 0px;
    position: relative;
  }

  .branch-showcase {
    position: relative;
    min-height: 580px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .branch-card-full {
    position: relative;
    min-height: 580px;
    display: flex;
    align-items: stretch;
  }

  // 切换时叠放避免白闪
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    position: absolute;
    inset: 0;
  }

  .branch-bg {
    position: absolute;
    inset: 0;
    .branch-bg-image {
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      transition: transform 8s ease;
      animation: slowZoom 20s ease-in-out infinite alternate, bgFadeIn 0.6s ease;
    }
    .branch-bg-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, rgba(10,22,40,0.55) 0%, rgba(10,22,40,0.3) 45%, rgba(10,22,40,0.05) 100%);
    }
  }

  @keyframes slowZoom {
    from { transform: scale(1); }
    to { transform: scale(1.08); }
  }

  @keyframes bgFadeIn {
    from { opacity: 0; transform: scale(1.05); }
    to { opacity: 1; transform: scale(1); }
  }

  .branch-content-layer {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px;
    color: #fff;
    width: 55%;
  }

  .branch-city-name {
    font-size: 2.4rem;
    font-weight: 800;
    margin-bottom: 1rem;
    position: relative;
    &::after {
      content: '';
      display: block;
      width: 60px;
      height: 4px;
      background: linear-gradient(to right, #0052CC, #3B7FFF);
      margin-top: 12px;
      border-radius: 2px;
    }
  }

  .branch-description {
    font-size: 1.05rem;
    line-height: 1.8;
    opacity: 0.9;
    margin-bottom: 2rem;
  }

  .branch-metrics {
    display: flex;
    gap: 30px;
    .metric-item {
      display: flex;
      align-items: center;
      gap: 12px;
      background: rgba(255,255,255,0.1);
      backdrop-filter: blur(10px);
      padding: 16px 24px;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.15);
    }
    .metric-icon {
      font-size: 1.5rem;
      color: #3B7FFF;
    }
    .metric-label {
      display: block;
      font-size: 0.8rem;
      opacity: 0.7;
      margin-bottom: 2px;
    }
    .metric-value {
      font-size: 1.3rem;
      font-weight: 700;
    }
  }

  .branch-thumbnails {
    display: flex;
    gap: 12px;
    margin-top: 30px;
    .thumb-item {
      width: 80px;
      height: 60px;
      border-radius: 8px;
      overflow: hidden;
      border: 2px solid rgba(255,255,255,0.3);
      cursor: pointer;
      transition: all .3s ease;
      &.active, &:hover {
        border-color: #3B7FFF;
        transform: scale(1.05);
      }
    }
  }

  .branch-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.2);
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .3s ease;
    &:hover {
      background: rgba(0,82,204,0.8);
      transform: translateY(-50%) scale(1.1);
    }
    &-prev { left: 20px; }
    &-next { right: 20px; }
  }

  .branch-dots {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    z-index: 10;
    .dot {
      position: relative;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: rgba(255,255,255,0.4);
      cursor: pointer;
      transition: all .4s cubic-bezier(.4,0,.2,1);
      &.active {
        width: 36px;
        border-radius: 6px;
        background: #3B7FFF;
      }
      &:hover .dot-label {
        opacity: 1;
        transform: translateX(-50%) translateY(-8px);
      }
      .dot-label {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(0);
        white-space: nowrap;
        font-size: 12px;
        color: #fff;
        background: rgba(0,0,0,0.7);
        padding: 4px 10px;
        border-radius: 4px;
        opacity: 0;
        transition: all .3s ease;
        pointer-events: none;
      }
    }
  }

  // 切换动画
  .slide-left-enter-active, .slide-left-leave-active,
  .slide-right-enter-active, .slide-right-leave-active {
    transition: all .5s cubic-bezier(.4,0,.2,1);
  }
  .slide-left-enter { transform: translateX(60px); opacity: 0; }
  .slide-left-leave-to { transform: translateX(-60px); opacity: 0; }
  .slide-right-enter { transform: translateX(-60px); opacity: 0; }
  .slide-right-leave-to { transform: translateX(60px); opacity: 0; }

  /* 资质证书 Gallery 样式 */
  .certificates-section {
    padding: 60px 0;
    position: relative;
    overflow: hidden;
  }

  .cert-gallery {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    padding: 20px;
  }

  .cert-card {
    transition: all .5s cubic-bezier(.4,0,.2,1);
    animation: certFlyIn .8s cubic-bezier(.16,1,.3,1) var(--delay) both;

    &:hover {
      transform: scale(1.08) translateY(-8px);
      z-index: 10;
      .cert-card-inner {
        box-shadow: 0 20px 50px rgba(0,82,204,0.2);
      }
    }
  }

  .cert-card-inner {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    border: 1px solid rgba(0,82,204,0.08);
    transition: all .4s ease;
    text-align: center;

    .cert-img {
      width: 100%;
      height: 220px;
      object-fit: contain;
      margin-bottom: 16px;
    }

    .cert-label {
      font-size: 14px;
      font-weight: 600;
      color: $heading;
      padding-top: 12px;
      border-top: 1px solid rgba(0,82,204,0.08);
    }
  }

  @keyframes certFlyIn {
    from {
      opacity: 0;
      transform: translateY(40px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  // 响应式
  @media (max-width: 1100px) {
    .cert-gallery { grid-template-columns: repeat(3, 1fr); }
  }
  @media (max-width: 768px) {
    .cert-gallery { grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .branch-content-layer { width: 100%; padding: 30px; }
    .branch-metrics { flex-direction: column; gap: 16px; }
    .branch-thumbnails { display: none; }
  }
  @media (max-width: 480px) {
    .cert-gallery { grid-template-columns: 1fr; }
  }
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: $light;
  color: $silver;
  font-size: 16px;
  font-weight: 300;
  border-radius: 12px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式调整 */
@media (max-width: 1440px) {
  .hero-banner .hero-container,
  .main-content {
    max-width: 1200px;
  }
}

@media (max-width: 1200px) {
  .hero-banner {
    height: 60vh;
    min-height: 500px;
  }
  .main-content {
    padding: 60px 5%;
    .section {
      margin-bottom: 80px;
    }
  }
}

@media (max-width: 1100px) {
  .hero-banner {
    height: 55vh;
    min-height: 450px;
  }
  .main-content {
    padding: 50px 5%;
    .section {
      margin-bottom: 70px;
    }
    .section-header {
      margin-bottom: 40px;
    }
  }
}

@media (max-width: 768px) {
  .hero-banner {
    height: 50vh;
    min-height: 400px;
    .hero-container {
      padding: 0 20px;
    }
    .hero-content {
      h1 {
        font-size: 2rem;
      }
      .subtitle {
        font-size: 1rem;
      }
    }
  }
  .main-content {
    padding: 40px 20px;
    .section {
      margin-bottom: 60px;
    }
    .section-header {
      margin-bottom: 30px;
      .section-title {
        font-size: 2rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .hero-banner {
    height: 45vh;
    min-height: 350px;
    .hero-content {
      h1 {
        font-size: 1.8rem;
      }
      .subtitle {
        font-size: 0.9rem;
      }
    }
  }
  .main-content {
    padding: 30px 15px;
    padding-left: 0px;
    .section {
      margin-bottom: 50px;
    }
    .section-header {
      margin-bottom: 25px;
      .section-title {
        font-size: 1.6rem;
        &::after {
          width: 60px;
          height: 3px;
        }
      }
    }
  }
}

@media (max-width: 1100px) {
  .intro-horizontal {
    gap: 14px;
  }
}

@media (max-width: 480px) {
  .hero-banner {
    min-height: 60vh;
    .hero-content h1 {
      font-size: 1.8rem;
    }
  }
  .section-header .section-title {
    font-size: 1.8rem;
  }
}

/* 打印样式 */
@media print {
  .hero-banner {
    display: none;
  }
  .main-content {
    padding: 0 !important;
  }
  .section {
    page-break-inside: avoid;
  }
}
}

svg {
fill: $body;
}

@media screen and (max-width: 768px) {
.container {
  padding-left: 16px !important;
  padding-right: 16px !important;
}
.intro-horizontal {
  flex-direction: column !important;
  align-items: stretch !important;
}
.intro-video-col,
.intro-card-col,
.intro-service-col {
  width: 100% !important;
  padding-right: 0 !important;
}
.video-wrapper {
  height: 220px !important;
}
.intro-video {
  width: 100% !important;
  height: 100% !important;
  display: block !important;
}
}

::v-deep p {
word-break: break-word;
hyphens: auto;
}

/* ===================================================================
   REVEAL ANIMATION (IntersectionObserver .reveal)
   =================================================================== */
.reveal {
opacity: 0;
transform: translateY(30px);
transition: opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
            transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
transition-delay: var(--delay, 0s);

&.revealed {
  opacity: 1;
  transform: translateY(0);
}
}
</style>